/**
 * DemoBanner — shown in the app chrome whenever the host is on demo.padpulse.io.
 *
 * Renders:
 *   • A fixed banner across the top of the page ("DEMO SANDBOX").
 *   • A "Reset Demo" button with a confirmation dialog that calls
 *     POST /api/admin/demo/reset and shows feedback.
 *
 * Detection: reads `window.__PADPULSE_DEMO__` which is set by demo-main.tsx
 * before the app mounts. This avoids any prop-drilling or context changes to
 * the existing app shell.
 */

import { useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { RotateCcw, AlertTriangle, CheckCircle2 } from "lucide-react";

export function isDemoMode(): boolean {
  return !!(window as any).__PADPULSE_DEMO__;
}

type ResetState = "idle" | "pending" | "done" | "error";

type ResetProgress = {
  status: "idle" | "running" | "done" | "error";
  phase: "idle" | "deleting" | "loading" | "done";
  totalTables: number;
  completedTables: number;
  currentTable: string | null;
  currentRows: number | null;
  failedTable: string | null;
  error: string | null;
};

// Turn a raw snake_case table name into something a booth operator can read.
function prettyTable(table: string | null): string {
  if (!table) return "";
  return table.replace(/_/g, " ");
}

export function DemoBanner() {
  const [open, setOpen] = useState(false);
  const [resetState, setResetState] = useState<ResetState>("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [progress, setProgress] = useState<ResetProgress | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  if (!isDemoMode()) return null;

  function stopPolling() {
    if (pollRef.current != null) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }

  function startPolling() {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch("/api/demo/reset-status", { credentials: "include" });
        if (!res.ok) return;
        const data = (await res.json()) as ResetProgress;
        setProgress(data);
        if (data.status === "done" || data.status === "error") {
          stopPolling();
        }
      } catch {
        // transient poll failure — keep trying until the POST resolves
      }
    }, 350);
  }

  async function handleReset() {
    setResetState("pending");
    setErrorMsg(null);
    setProgress(null);
    startPolling();
    try {
      const res = await fetch("/api/demo/reset", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || `Reset failed (${res.status})`);
      }
      stopPolling();
      setResetState("done");
      // Reload after a short delay so the UI reflects fresh data
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (err: any) {
      stopPolling();
      setErrorMsg(err?.message || "Reset failed");
      setResetState("error");
    }
  }

  function handleDialogClose(v: boolean) {
    if (resetState === "pending") return; // block close during reset
    stopPolling();
    setOpen(v);
    if (!v) {
      setResetState("idle");
      setErrorMsg(null);
      setProgress(null);
    }
  }

  // Percent complete for the bar. During the brief delete sweep we show a small
  // indeterminate-feeling sliver so the bar is never stuck at 0.
  const pct =
    progress && progress.totalTables > 0
      ? Math.round((progress.completedTables / progress.totalTables) * 100)
      : progress?.phase === "deleting"
        ? 5
        : 0;

  return (
    <>
      {/* Fixed banner across the very top */}
      <div
        data-testid="demo-banner"
        className="fixed inset-x-0 top-0 z-[9999] flex items-center justify-between bg-brand-accent px-4 py-1.5 text-brand-accent-foreground shadow-sm"
        style={{ height: 36 }}
      >
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span className="text-sm font-semibold tracking-wide">DEMO SANDBOX — no real data, no real actions</span>
        </div>

        <AlertDialog open={open} onOpenChange={handleDialogClose}>
          <AlertDialogTrigger asChild>
            <Button
              size="sm"
              variant="outline"
              data-testid="demo-reset-button"
              className="h-7 gap-1.5 border-brand-accent-foreground/40 bg-transparent text-brand-accent-foreground hover:bg-brand-accent-foreground/10"
              onClick={() => setOpen(true)}
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Reset Demo
            </Button>
          </AlertDialogTrigger>

          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Reset Demo?</AlertDialogTitle>
              <AlertDialogDescription>
                This will restore all demo data to the last saved snapshot. Any changes
                made during this demo session will be wiped. The page will reload
                automatically when done.
              </AlertDialogDescription>
            </AlertDialogHeader>

            {resetState === "pending" && (
              <div className="space-y-2" data-testid="demo-reset-progress">
                <Progress value={pct} aria-label="Demo reset progress" />
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <span data-testid="text-reset-progress-label">
                    {progress?.phase === "deleting"
                      ? "Clearing old demo data…"
                      : progress?.currentTable
                        ? `Loading ${prettyTable(progress.currentTable)}${
                            progress.currentRows != null ? ` (${progress.currentRows} rows)` : ""
                          }…`
                        : "Starting reset…"}
                  </span>
                  {progress && progress.totalTables > 0 && (
                    <span className="tabular-nums" data-testid="text-reset-progress-count">
                      {progress.completedTables}/{progress.totalTables}
                    </span>
                  )}
                </div>
              </div>
            )}

            {resetState === "error" && errorMsg && (
              <p
                className="rounded border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
                data-testid="text-reset-error"
              >
                {progress?.failedTable
                  ? `Reset failed while loading ${prettyTable(progress.failedTable)}: ${errorMsg}`
                  : errorMsg}
              </p>
            )}

            {resetState === "done" && (
              <p className="flex items-center gap-2 rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
                <CheckCircle2 className="h-4 w-4 shrink-0" />
                Demo reset complete. Reloading…
              </p>
            )}

            <AlertDialogFooter>
              <AlertDialogCancel disabled={resetState === "pending"}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                data-testid="demo-reset-confirm"
                disabled={resetState === "pending" || resetState === "done"}
                onClick={(e) => {
                  e.preventDefault();
                  void handleReset();
                }}
              >
                {resetState === "pending" ? "Restoring…" : "Reset Demo"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {/* Spacer so content below the banner isn't hidden */}
      <div style={{ height: 36 }} aria-hidden />
    </>
  );
}
